/**
 * Resolve map center/zoom from URL pathname for first paint
 * (before page useEffects set selectedLocation).
 */
import { WORLD_COUNTRIES } from "../data/world-countries";
import {
  getCountryMapCenter,
  getCountryMapZoom,
} from "./country-bounds";
import {
  getEuropeHighlightBySlug,
  getHighlightsByActivities,
  getHighlightsMapFrame,
  getRegionCentroid,
  getHighlightDetailMapFrame,
} from "./europe-highlights";
import type { HighlightActivity } from "../types/europe-highlights";

export type MapFrame = {
  lat: number;
  lng: number;
  zoom: number;
};

const DEFAULT_FRAME: MapFrame = {
  lat: 50.879,
  lng: 2.6997,
  zoom: 3,
};

const COUNTRY_SLUGS = new Set(
  WORLD_COUNTRIES.map((c) => c.slug.toLowerCase())
);
// UK nations / special locality countries used on the site
for (const s of ["scotland", "england", "wales", "northern-ireland"]) {
  COUNTRY_SLUGS.add(s);
}

const SKIP_ROOTS = new Set([
  "about",
  "contact-us",
  "events",
  "stories",
  "category",
  "tag",
  "event",
  "story",
  "features",
  "collections",
  "types",
  "login",
  "register",
  "dashboard",
  "create-point",
  "edit-point",
  "places-around-me",
  "world",
  "currency",
  "language",
  "page",
  "blog",
]);

const ACTIVITY_SLUGS = new Set([
  "climbing",
  "ferrata",
  "rafting",
  "trail",
  "paragliding",
  "forest",
  "mountain",
  "lake",
  "camping",
]);

function segments(pathname: string): string[] {
  return (pathname || "/")
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Best-effort map frame for a path. Returns null for home / non-geo pages
 * so callers can keep the default Europe overview.
 */
export function resolveMapFrameFromPath(
  pathname: string | null | undefined
): MapFrame | null {
  const parts = segments(pathname || "/");
  if (!parts.length) return null;

  // /highlights/{slug}/ or /highlights/scotland/
  if (parts[0] === "highlights") {
    const slug = parts[1];
    if (!slug || slug === "scotland") {
      const list = getHighlightsByActivities(
        ["climbing", "trail", "lake", "mountain", "forest", "ferrata"],
        { includeCityBases: false }
      ).filter((h) => (slug === "scotland" ? h.country === "scotland" : true));
      const scotland = list.filter((h) => h.country === "scotland");
      return getHighlightsMapFrame(
        slug === "scotland" && scotland.length ? scotland : list
      );
    }
    const h = getEuropeHighlightBySlug(slug);
    if (h) {
      const frame = getHighlightDetailMapFrame(h);
      return { lat: frame.lat, lng: frame.lng, zoom: frame.zoom };
    }
    return null;
  }

  // /activity/{slug}/
  if (parts[0] === "activity") {
    const act = parts[1] as HighlightActivity | undefined;
    if (!act || !ACTIVITY_SLUGS.has(act)) {
      return { lat: 48, lng: 10, zoom: 4 };
    }
    const list = getHighlightsByActivities([act], { includeCityBases: false });
    return getHighlightsMapFrame(list);
  }

  // /point/{slug}/ — no static coords without WP; keep default
  if (parts[0] === "point") return null;

  if (SKIP_ROOTS.has(parts[0])) return null;

  // /{country}/ or /{country}/{locality}/…
  const countrySlug = parts[0].toLowerCase();
  if (!COUNTRY_SLUGS.has(countrySlug) && !getCountryMapCenter(countrySlug)) {
    return null;
  }

  if (parts.length === 1) {
    const center = getCountryMapCenter(countrySlug);
    if (!center) return null;
    return {
      lat: center.lat,
      lng: center.lng,
      zoom: getCountryMapZoom(countrySlug, 5),
    };
  }

  // Locality / region — but /{country}/{region}/{point}/ is a detail page:
  // don't yank zoom to region level (neighbor point clicks must only pan).
  if (parts.length >= 3) {
    return null;
  }

  const localitySlug = parts[1].toLowerCase();
  // Skip taxonomy tails like /switzerland/tag/…
  if (localitySlug === "tag" || localitySlug === "features") {
    const center = getCountryMapCenter(countrySlug);
    if (!center) return null;
    return {
      lat: center.lat,
      lng: center.lng,
      zoom: getCountryMapZoom(countrySlug, 5),
    };
  }

  const centroid = getRegionCentroid(countrySlug, localitySlug);
  if (centroid) {
    return {
      lat: centroid.lat,
      lng: centroid.lng,
      zoom: Math.min(14, getCountryMapZoom(countrySlug, 7) + 2),
    };
  }

  // Unknown second segment — often a point under the country
  // (`/{country}/{point}/`). Don't yank to country zoom; the page owns the camera.
  return null;
}

export function resolveMapFrameOrDefault(
  pathname: string | null | undefined
): MapFrame {
  return resolveMapFrameFromPath(pathname) || DEFAULT_FRAME;
}

export { DEFAULT_FRAME };
